import React, { useEffect, useState } from "react";

import {
  getRestrictions,
  createRestriction,
  updateRestriction,
  deleteRestriction,
  assignRestrictionToUser,
  getUserRestrictions,
  removeUserRestriction,
} from "../services/restrictionApi";

function RestrictionsPage() {
  const [restrictions, setRestrictions] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    active: true,
  });

  const [assignForm, setAssignForm] = useState({
    userId: "",
    restrictionId: "",
  });

  const [userRestrictions, setUserRestrictions] = useState([]);

  useEffect(() => {
    loadRestrictions();
  }, []);

  const loadRestrictions = async () => {
    const res = await getRestrictions();
    setRestrictions(res.data);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const saveRestriction = async (e) => {
    e.preventDefault();

    if (editingId) {
      await updateRestriction(editingId, form);
    } else {
      await createRestriction(form);
    }

    setForm({
      code: "",
      name: "",
      description: "",
      active: true,
    });

    setEditingId(null);
    loadRestrictions();
  };

  const editRestriction = (restriction) => {
    setEditingId(restriction.id);
    setForm({
      code: restriction.code,
      name: restriction.name,
      description: restriction.description || "",
      active: restriction.active,
    });
  };

  const removeRestriction = async (id) => {
    if (!window.confirm("Delete this restriction?")) return;
    await deleteRestriction(id);
    loadRestrictions();
  };

  const assignRestriction = async (e) => {
    e.preventDefault();

    await assignRestrictionToUser({
      userId: Number(assignForm.userId),
      restrictionId: Number(assignForm.restrictionId),
    });

    loadUserRestrictions(assignForm.userId);
  };

  const loadUserRestrictions = async (userId) => {
    if (!userId) return;

    const res = await getUserRestrictions(userId);
    setUserRestrictions(res.data);
  };

  const removeAssignedRestriction = async (id) => {
    await removeUserRestriction(id);
    loadUserRestrictions(assignForm.userId);
  };

  return (
    <div className="container mt-4">
      <h3>User Restrictions</h3>

      <div className="card shadow-sm mb-4">
        <div className="card-header">
          {editingId ? "Update Restriction" : "Create Restriction"}
        </div>

        <div className="card-body">
          <form onSubmit={saveRestriction}>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Code</label>
                <input
                  name="code"
                  className="form-control"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="CREATE_ORDER"
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Name</label>
                <input
                  name="name"
                  className="form-control"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Create Order"
                  required
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Description</label>
                <input
                  name="description"
                  className="form-control"
                  value={form.description}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-2 mb-3 d-flex align-items-end">
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="active"
                    className="form-check-input"
                    checked={form.active}
                    onChange={handleChange}
                  />
                  <label className="form-check-label">Active</label>
                </div>
              </div>
            </div>

            <button className="btn btn-primary me-2">
              {editingId ? "Update" : "Create"}
            </button>

            {editingId && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setEditingId(null);
                  setForm({
                    code: "",
                    name: "",
                    description: "",
                    active: true,
                  });
                }}
              >
                Cancel
              </button>
            )}
          </form>
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header">Restrictions List</div>

        <div className="card-body">
          <table className="table table-bordered table-hover">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th width="180">Actions</th>
              </tr>
            </thead>

            <tbody>
              {restrictions.map((item) => (
                <tr key={item.id}>
                  <td>{item.code}</td>
                  <td>{item.name}</td>
                  <td>{item.description}</td>
                  <td>
                    {item.active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-warning me-2"
                      onClick={() => editRestriction(item)}
                    >
                      Edit
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => removeRestriction(item.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {restrictions.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    No restrictions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header">Assign Restriction To User</div>

        <div className="card-body">
          <form onSubmit={assignRestriction}>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">User ID</label>
                <input
                  type="number"
                  className="form-control"
                  value={assignForm.userId}
                  onChange={(e) => {
                    setAssignForm({
                      ...assignForm,
                      userId: e.target.value,
                    });

                    loadUserRestrictions(e.target.value);
                  }}
                  required
                />
              </div>

              <div className="col-md-5 mb-3">
                <label className="form-label">Restriction</label>
                <select
                  className="form-control"
                  value={assignForm.restrictionId}
                  onChange={(e) =>
                    setAssignForm({
                      ...assignForm,
                      restrictionId: e.target.value,
                    })
                  }
                  required
                >
                  <option value="">Select restriction</option>
                  {restrictions
                    .filter((r) => r.active)
                    .map((restriction) => (
                      <option key={restriction.id} value={restriction.id}>
                        {restriction.name} ({restriction.code})
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-md-3 mb-3 d-flex align-items-end">
                <button className="btn btn-success w-100">
                  Assign Restriction
                </button>
              </div>
            </div>
          </form>

          <h5 className="mt-4">User Assigned Restrictions</h5>

          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Restriction</th>
                <th>Code</th>
                <th>Status</th>
                <th width="120">Action</th>
              </tr>
            </thead>

            <tbody>
              {userRestrictions.map((item) => (
                <tr key={item.id}>
                  <td>{item.restriction.name}</td>
                  <td>{item.restriction.code}</td>
                  <td>
                    {item.active ? (
                      <span className="badge bg-danger">Restricted</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => removeAssignedRestriction(item.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}

              {userRestrictions.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center">
                    No restriction assigned to this user
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RestrictionsPage;